import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { nftSchema, redeemPreviewSchema, type BootstrapResponse, type InventoryResponse } from "../../../api/schemas";
import { getAddress } from "viem";
import { readRedeemFeeQuote, type RedeemFeeRoute } from "../../../wallet/redeem-fee-quote";
import { ContextChangedError } from "../../../wallet/wallet-operation";
import { selectRedeemRoute, isDirectRedeemRoute } from "./redeem-route";
import { ELYSIUM } from "../../../config/elysium";
import { readElysiumRedeemPreview } from "../../../wallet/elysium-redeem";
import { assertRedeemNftDetail } from "./redeem-nft-validation";
import type { WalletSessionController, WalletSessionSnapshot } from "../../../wallet/wallet-session";
import { sameAddress } from "../../../state/model-helpers";
import type { ActionContext } from "../../../state/action-context";

type Options = Pick<ActionContext, "contextKey" | "readContextKey" | "activeContext"> & {
  walletController: WalletSessionController;
  walletSnapshot: WalletSessionSnapshot;
  inventory: InventoryResponse | null;
  inventoryRevision: number;
  bootstrap?: BootstrapResponse | null;
  feeRoute?: RedeemFeeRoute | null;
  feeRouteUnavailable?: string | null;
};
export type RedeemPreview = import("../types").InventoryPayoutPreview;

export function useRedeemPreview({ walletController, walletSnapshot, inventory, inventoryRevision, bootstrap, feeRoute = null, feeRouteUnavailable = null,
  contextKey, readContextKey, activeContext }: Options) {
  const [storedPreviews, setRedeemPreviews] = useState<Record<string, RedeemPreview & { contextKey: string }>>({});
  const routeKey = JSON.stringify([feeRoute, feeRouteUnavailable]);
  const redeemPreviewGeneration = useRef(0);
  const redeemPreviewAbort = useRef<AbortController | null>(null);
  const currentInventory = useRef(inventory);
  currentInventory.current = inventory;
  useEffect(() => {
    redeemPreviewGeneration.current++;
    redeemPreviewAbort.current?.abort();
    setRedeemPreviews({});
  }, [inventoryRevision]);
  const redeemPreviews = useMemo(() => Object.fromEntries(Object.entries(storedPreviews)
    .filter(([, preview]) => preview.contextKey === contextKey)), [storedPreviews, contextKey]);
  useEffect(() => () => { redeemPreviewAbort.current?.abort(); }, []);
  useEffect(() => {
    redeemPreviewGeneration.current++;
    redeemPreviewAbort.current?.abort();
    setRedeemPreviews({});
  }, [contextKey, routeKey]);

  const requestRedeemPreview = useCallback(async (tokenId: string) => {
    const requestContext = contextKey;
    if (requestContext !== readContextKey()) return;
    const account = walletSnapshot.account;
    const direct = bootstrap?.contracts.mintChainId === ELYSIUM.chainId;
    const nft = inventory?.items?.find((item) => item.tokenId === tokenId);
    if (!account || !direct && !walletSnapshot.authenticated || !nft?.sourceToken || !nft.owner ||
      nft.status !== "FINALIZED" || !sameAddress(nft.owner, account)) return;
    const generation = ++redeemPreviewGeneration.current;
    redeemPreviewAbort.current?.abort();
    const abort = new AbortController();
    redeemPreviewAbort.current = abort;
    // Remove the previous estimate before reading: an RPC error is unavailable, not an old or zero quote.
    setRedeemPreviews(current => { const next = { ...current }; delete next[tokenId]; return next; });
    const guard = () => {
      abort.signal.throwIfAborted();
      if (requestContext !== readContextKey() || generation !== redeemPreviewGeneration.current) throw new ContextChangedError();
    };
    const load = async () => {
      if (feeRouteUnavailable) throw new Error(feeRouteUnavailable);
      if (direct) {
        const route = selectRedeemRoute(bootstrap ?? null, nft.sourceToken!, feeRoute);
        const provider = walletController.getProvider();
        if (!provider || !isDirectRedeemRoute(route)) throw new Error("DIRECT_REDEEM_ROUTE_UNAVAILABLE");
        const result = await readElysiumRedeemPreview(provider, { account: getAddress(account), tokenId: BigInt(tokenId),
          minimum: 0n, route }, guard, { signal: abort.signal });
        return { available: true, redeemable: true, sourceToken: route.sourceToken, expectedPayoutWei: result.payout.toString(), route: "DIRECT" as const };
      }
      if (!feeRoute) {
        const legacy = await walletController.api.get(`/nfts/${tokenId}/redeem-preview`, redeemPreviewSchema, abort.signal);
        return { available: legacy.available, redeemable: legacy.redeemable, sourceToken: legacy.sourceToken,
          expectedPayoutWei: legacy.expectedPayoutWei, route: "LEGACY" as const };
      }
      const route = selectRedeemRoute(bootstrap ?? null, nft.sourceToken!, feeRoute);
      const provider = walletController.getProvider();
      if (!provider || !route || isDirectRedeemRoute(route)) throw new Error("Sweep quote provider unavailable");
      if (!sameAddress(nft.collectionAddress, route.collection) || !sameAddress(nft.sourceToken, route.sourceToken)) throw new Error("NFT preview deployment mismatch");
      const detail = await walletController.api.get(`/nfts/${tokenId}`, nftSchema, abort.signal);
      guard();
      assertRedeemNftDetail(detail, { tokenId, account, chainId: route.chainId, collection: route.collection, sourceToken: route.sourceToken });
      const result = await readRedeemFeeQuote(provider, { account: getAddress(account), tokenId: BigInt(tokenId),
        route, contextRevision: requestContext }, guard, { signal: abort.signal });
      if (!result.available) throw new Error(result.message);
      return { available: true, redeemable: true, sourceToken: result.quote.context.sourceToken,
        expectedPayoutWei: result.quote.payoutWei.toString(), route: "WITH_FEES" as const,
        vaultPoolWei: result.quote.vaultPoolWei.toString(), poolAfterSweepWei: result.quote.poolAfterSweepWei.toString() };
    };
    const preview = await load().catch((error: unknown) => {
      if (abort.signal.aborted || requestContext !== readContextKey() || generation !== redeemPreviewGeneration.current) return null;
      throw error;
    });
    if (!preview) return;
    const currentNft = currentInventory.current?.items?.find((item) => item.tokenId === tokenId);
    const liveWallet = walletController.getSnapshot();
    if (abort.signal.aborted || requestContext !== readContextKey() || generation !== redeemPreviewGeneration.current || !direct && !liveWallet.authenticated ||
      !sameAddress(liveWallet.account, account) || !sameAddress(activeContext.current.account, account) ||
      !sameAddress(activeContext.current.token, nft.sourceToken) || !currentNft || currentNft !== nft ||
      !sameAddress(currentNft.collectionAddress, nft.collectionAddress) ||
      !sameAddress(currentNft.sourceToken, nft.sourceToken) ||
      (preview.sourceToken !== undefined && preview.sourceToken !== null &&
        !sameAddress(preview.sourceToken, nft.sourceToken))) return;
    if (!preview.available || !preview.expectedPayoutWei) {
      setRedeemPreviews((current) => {
        const next = { ...current };
        delete next[tokenId];
        return next;
      });
      return;
    }
    setRedeemPreviews((current) => ({ ...current,
      [tokenId]: { contextKey: requestContext, payoutWei: preview.expectedPayoutWei!, redeemable: Boolean(preview.redeemable),
        route: preview.route,
        ...("vaultPoolWei" in preview ? { vaultPoolWei: preview.vaultPoolWei, poolAfterSweepWei: preview.poolAfterSweepWei } : {}) } }));
  }, [walletController, activeContext, inventory?.items, walletSnapshot.account, walletSnapshot.authenticated, contextKey, readContextKey, bootstrap, feeRoute, feeRouteUnavailable]);

  return { redeemPreviews, requestRedeemPreview };
}
